import { deviceApi } from '@accedo/ctv-device-api';
import { logger } from '@accedo/logger';
import { createContext, FunctionalComponent } from 'preact';
import { useContext, useEffect } from 'preact/hooks';
import { isDeviceIQ, isDeviceMagneto } from '##/utils/device';
import { getAccessToken } from '../authentication/Authentication';

export type GlassGuidedSetupData = {
  event: string;
  deviceId?: string;
  refreshToken?: string;
  accessToken?: string;
};

type PlatformMessage = {
  event: string;
  data?: unknown;
};

interface PlatformMessagingContextValue {
  sendMessageToPlatform: (message: PlatformMessage) => void;
}

const PlatformMessagingContext = createContext<
  PlatformMessagingContextValue | undefined
>(undefined);

export const PlatformMessagingProvider: FunctionalComponent = (props) => {
  const { children } = props;
  let foxtelMessagingInstance: FoxtelAppMessaging;

  const sendMessageToPlatform = async (
    messageData: PlatformMessage,
  ): Promise<void> => {
    const message = JSON.stringify(messageData);

    if (isDeviceIQ()) {
      if (foxtelMessagingInstance) {
        foxtelMessagingInstance.sendMessage(message);
      } else {
        logger.warn(
          'Unsuccessfully tried to send message to Foxtel, no instance available',
          message,
        );
      }
    } else if (isDeviceMagneto()) {
      const guidedSetupEvent: GlassGuidedSetupData = {
        event: messageData.event,
      };

      if (messageData.event === 'appSignOnSuccessful') {
        guidedSetupEvent.deviceId = await deviceApi.device.id?.();
        guidedSetupEvent.refreshToken = localStorage.getItem('refreshToken') || undefined;
        guidedSetupEvent.accessToken = getAccessToken() || undefined;
      }
      window.parent.postMessage(JSON.stringify(guidedSetupEvent), '*');
  }
};

const onMessageReceived = (messageData: string): void => {
    try {
      logger.info('Message received from Foxtel', JSON.parse(messageData));
    } catch {
      logger.warn('Message received from Foxtel is invalid');
    }
  };

  const setupPlatformCommunication = async (): Promise<void> => {
    if (isDeviceIQ()) {
      if (window.FoxtelAppMessaging) {
        try {
          foxtelMessagingInstance = new window.FoxtelAppMessaging(
            onMessageReceived,
          );
        } catch (err) {
          logger.error(err);
        }
      } else {
        logger.warn('No foxtel messaging service on window');
      }
    }
  };

  useEffect(() => {
    setupPlatformCommunication();
  }, []);

  return (
    <PlatformMessagingContext.Provider value={{ sendMessageToPlatform }}>
      {children}
    </PlatformMessagingContext.Provider>
  );
};

export const usePlatformMessaging = (): PlatformMessagingContextValue => {
  const context = useContext(PlatformMessagingContext);

  if (!context) {
    throw new Error('Component beyond FoxtelMessagingContext');
  }

  return context;
};
